export const ACTIVITY_PRICE_TYPE_PER_PARTICIPANT = 'PER_PARTICIPANT';
export const ACTIVITY_PRICE_TYPE_PRICE_RANGE = 'RANGE';
export const ACTIVITY_PRICE_TYPE_PRICE_SCALE = 'SCALE';
export const ACTIVITY_PRICE_TYPES = [
    ACTIVITY_PRICE_TYPE_PER_PARTICIPANT,
    ACTIVITY_PRICE_TYPE_PRICE_RANGE,
    ACTIVITY_PRICE_TYPE_PRICE_SCALE,
] as const;
export type ActivityPriceType = 'PER_PARTICIPANT' | 'RANGE' | 'SCALE';

/** Group size every search path prices with when the visitor has not chosen one.
 * The results header states it and each card's total is derived from it, so both
 * read this rather than their own literal. 79% of searches are for 20 or fewer,
 * with 16-20 the biggest bucket (PostHog, 90 days to 1 Sep 2026). */
export const DEFAULT_DISPLAY_PARTICIPANTS = 20;

export const ACTIVITY_LOCATION_TYPES = ['INDOOR', 'OUTDOOR', 'VIRTUAL'] as const;
export type ActivityLocationType = (typeof ACTIVITY_LOCATION_TYPES)[number];
export const ACTIVITY_LOCATION_FILTER_TYPES = ['FIXED', 'CUSTOM'] as const;
export type ActivityLocationFilterType = (typeof ACTIVITY_LOCATION_FILTER_TYPES)[number];

export const ACTIVITY_STATUS = [
    /** This is the new 'Draft' status */
    'CREATING',
    /** @deprecated this was only used in WP */
    'DRAFT',
    'PENDING',
    'PUBLISHED',
    'ARCHIVED',
    'DELETED',
] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUS)[number];
export const ACTIVITY_VARIANT_STATUS = ['ACTIVE', 'DELETED'] as const;
export type ActivityVariantStatus = (typeof ACTIVITY_VARIANT_STATUS)[number];
export const ACTIVITY_CLEANLINESS_TYPES = ['DIRTY', 'WET', 'CLEAN'] as const;
export type ActivityCleanliness = (typeof ACTIVITY_CLEANLINESS_TYPES)[number];

export const CART_ACTIVE = 'ACTIVE';
export const CART_CONVERTED = 'CONVERTED';
export const CART_STATUSES = [CART_ACTIVE, CART_CONVERTED] as const;
export type CartStatus = (typeof CART_STATUSES)[number];

export const CART_LOCATION_REGION = 'REGION';
export const CART_LOCATION_ADDRESS = 'ADDRESS';
export const CART_LOCATION_CUSTOM = 'CUSTOM';

export const CART_DURATION_HALF_DAY = 'HALF_DAY';
export const CART_DURATION_FULL_DAY = 'FULL_DAY';
export const CART_DURATION_MULTIPLE_DAYS = 'MULTIPLE_DAYS';
export const CART_DURATION_TYPES = [
    CART_DURATION_HALF_DAY,
    CART_DURATION_FULL_DAY,
    CART_DURATION_MULTIPLE_DAYS,
    'MULTIPLE_DAYS_WITH_OVERNIGHT_STAY',
] as const;
export type CartDuration = (typeof CART_DURATION_TYPES)[number];

// ── Accessibility ──────────────────────────────────────────────────────
export const ACTIVITY_ACCESSIBILITY_FEATURES = [
    // Physical
    'WHEELCHAIR_ACCESSIBLE',
    'ACCESSIBLE_PARKING',
    'ACCESSIBLE_TOILETS',
    'ELEVATOR_OR_RAMP',
    'MOBILITY_AID_AVAILABLE',
    // Sensory
    'BLIND_FRIENDLY',
    'DEAF_FRIENDLY',
    'LOW_STIMULUS',
    // Neurodiversity
    'STRUCTURED_PROGRAM',
    'FLEXIBLE_BREAKS',
    'ASSISTANCE_DOGS_WELCOME',
    'DIETARY_ACCOMMODATIONS',
    // Communication / Cognitive
    'SIMPLE_LANGUAGE',
    'EXTRA_TIME_ALLOWED',
] as const;
export type ActivityAccessibilityFeature = (typeof ACTIVITY_ACCESSIBILITY_FEATURES)[number];

// ── Target audience ("For whom?") ──────────────────────────────────────
export const ACTIVITY_TARGET_AUDIENCES = [
    'SENIOR_FRIENDLY',
    'CHILD_FRIENDLY',
    'STROLLER_FRIENDLY',
] as const;
export type ActivityTargetAudience = (typeof ACTIVITY_TARGET_AUDIENCES)[number];

export const SEASONS = ['WINTER', 'SPRING', 'SUMMER', 'FALL'] as const;
export type Season = (typeof SEASONS)[number];
export namespace Partner {
    export namespace ReferralPage {
        // NOTE: these currently have the same names as the design
        export const STATUS = ['ONLINE', 'CONCEPT'] as const;
        export type Status = (typeof STATUS)[number];
    }

    export namespace Transaction {
        export const TYPES = ['COMMISSION', 'WITHDRAWAL'] as const;
        export type Type = (typeof TYPES)[number];
    }

    export namespace Invoice {
        export const STATUSES = ['PENDING', 'DECLINED', 'ACCEPTED', 'PAYED'] as const;
        export type Status = (typeof STATUSES)[number];
    }
}

export namespace Deal {
    export const STATUSES = ['WON', 'PENDING'] as const;
    export type Status = (typeof Deal.STATUSES)[number];

    /** How the customer found out about Funkey, asked in the last step of the checkout. */
    export const CHECKOUT_SOURCES = [
        'SEARCH_ENGINE',
        'AI_CHAT',
        'YOUTUBE_AD',
        'SOCIAL_MEDIA',
        'WORD_OF_MOUTH',
        'RETURNING_CUSTOMER',
        'OTHER',
    ] as const;
    export type CheckoutSource = (typeof Deal.CHECKOUT_SOURCES)[number];
}

export const SORT_DIRECTION = ['ASC', 'DESC'] as const;
export type SortDirection = (typeof SORT_DIRECTION)[number];
